import React, { useState, useEffect, useCallback } from "react";
import {
  Autocomplete,
  ResourceList,
  ResourceItem,
  Text,
  InlineStack,
} from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { fetch } from "containers/products/products.service";
import { APP_CONFIG } from "config/app-config";
import debounce from "lodash/debounce";
import { Product } from "types/Product";
import CreateProductDialog from "./CreateProductDialog";
import { ProductLineFields, RemoveLineButton } from "./ProductLineFields";

const DEBOUNCE_DELAY = 300;

interface ProductsSelectProps {
  onChange: (products: Product[]) => void;
  initialProducts?: Product[];
  disabled?: boolean;
  error?: string;
  isRequired?: boolean;
  /** Offers to make a product from the search text when none fits. */
  allowCreate?: boolean;
  /**
   * Every line as it stands, unfinished ones too. `onChange` waits until all
   * lines have quantity and price, so a page keeping a draft needs this one.
   */
  onLinesChange?: (products: Product[]) => void;
}

const formatProductLabel = (product: Product) => {
  const codes = [product.sku, product.supplierSku].filter(
    (value): value is string => Boolean(value)
  );
  const codesSuffix = codes.length ? ` [${codes.join(" / ")}]` : "";
  return `${product.title}${codesSuffix}`;
};

const ProductsSelect: React.FC<ProductsSelectProps> = ({
  onChange,
  initialProducts = [],
  disabled = false,
  isRequired = false,
  allowCreate = false,
  onLinesChange,
}) => {
  const [selectedProducts, setSelectedProducts] =
    useState<Product[]>(initialProducts);
  const [searchResults, setSearchResults] = useState<Product[]>([]);
  const [query, setQuery] = useState("");
  const [defaultList, setDefaultList] = useState<Product[]>([]);
  /** The title the create dialog opens with; null while it is closed. */
  const [newProductTitle, setNewProductTitle] = useState<string | null>(null);
  const { companyId } = useParams();
  const { t } = useTranslation();

  useEffect(() => {
    const isValid = selectedProducts.every(
      (product) => product.quantity && product.price
    );
    if (isValid) {
      onChange(selectedProducts);
    }
  }, [selectedProducts, onChange]);

  useEffect(() => {
    onLinesChange?.(selectedProducts);
  }, [selectedProducts, onLinesChange]);

  useEffect(() => {
    const fetchInitialProducts = async () => {
      if (companyId) {
        const results: Product[] | undefined = await searchProducts(companyId);
        setDefaultList(results || []);
        setSearchResults(results || []);
      }
    };

    fetchInitialProducts();
  }, [companyId]);

  const handleSearchChange = useCallback(
    debounce(async (value: string) => {
      if (value.length > 0 && companyId) {
        const results: Product[] | undefined = await searchProducts(
          companyId,
          value
        );
        setSearchResults(results || defaultList);
      } else {
        setSearchResults(defaultList);
      }
    }, DEBOUNCE_DELAY),
    [companyId]
  );

  const onSearchInputChange = useCallback(
    (value: string) => {
      setQuery(value);
      if (value === "") {
        setSearchResults(defaultList);
      } else {
        handleSearchChange(value);
      }
    },
    [handleSearchChange, defaultList]
  );

  const handleAddProduct = useCallback((product: Product) => {
    setSelectedProducts((prev) => {
      if (prev.some((p) => p.product === product.product)) {
        return prev; // Product already selected, do not add
      }
      return [...prev, { ...product, quantity: 1, price: product.price }];
    });
  }, []);

  const handleRemoveProduct = useCallback((productId: string) => {
    setSelectedProducts((prev) =>
      prev.filter((product) => product.product !== productId)
    );
  }, []);

  const handleQuantityChange = useCallback(
    (productId: string, quantity: any) => {
      setSelectedProducts((prev) =>
        prev.map((product) =>
          product.product === productId ? { ...product, quantity } : product
        )
      );
    },
    []
  );

  const handlePriceChange = useCallback((productId: string, price: any) => {
    setSelectedProducts((prev) =>
      prev.map((product) =>
        product.product === productId ? { ...product, price } : product
      )
    );
  }, []);

  const handleNotesChange = useCallback(
    (productId: string, notes: string | "") => {
      setSelectedProducts((prev) =>
        prev.map((product) =>
          product.product === productId ? { ...product, notes } : product
        )
      );
    },
    []
  );

  const renderProductItem = useCallback(
    (product: Product) => {
      const quantity = product.quantity.toString();
      const price = product.price.toString();
      return (
        <ResourceItem
          key={product.product}
          id={product.product}
          onClick={() => {}}
          accessibilityLabel={`View details for ${product.title}`}
        >
          <InlineStack align="space-between" blockAlign="center" gap="200">
            <Text as="p" fontWeight="bold">
              {formatProductLabel(product)}
            </Text>
            <RemoveLineButton
              onRemove={() => handleRemoveProduct(product.product)}
            />
          </InlineStack>
          <ProductLineFields
            quantity={quantity}
            price={price}
            notes={product.notes || ""}
            onQuantityChange={(value) =>
              handleQuantityChange(product.product, value)
            }
            onPriceChange={(value) => handlePriceChange(product.product, value)}
            onNotesChange={(value) => handleNotesChange(product.product, value)}
          />
        </ResourceItem>
      );
    },
    [handleRemoveProduct, handleQuantityChange, handlePriceChange, handleNotesChange]
  );

  const searchText = query.trim();

  return (
    <>
      <Autocomplete
        options={searchResults.map((product) => ({
          value: product.product,
          label: formatProductLabel(product),
        }))}
        actionBefore={
          allowCreate && searchText
            ? {
                content: t("create_product_option", { text: searchText }),
                onAction: () => setNewProductTitle(searchText),
              }
            : undefined
        }
        selected={[]}
        onSelect={(selected) => {
          const product = searchResults.find((p) => p.product === selected[0]);
          if (product) handleAddProduct(product);
        }}
        textField={
          <Autocomplete.TextField
            label={t("search_products")}
            value={query}
            autoComplete="off"
            onChange={onSearchInputChange}
            requiredIndicator={isRequired}
            error={isRequired && selectedProducts.length == 0}
          />
        }
      />
      <ResourceList items={selectedProducts} renderItem={renderProductItem} />
      {newProductTitle !== null && companyId && (
        <CreateProductDialog
          companyId={companyId}
          initialTitle={newProductTitle}
          onClose={() => setNewProductTitle(null)}
          onCreated={(product) => {
            handleAddProduct(product);
            setNewProductTitle(null);
            onSearchInputChange("");
          }}
        />
      )}
    </>
  );
};

// Simulated AJAX search function
const searchProducts = async (
  companyId: string,
  query?: string
): Promise<Product[] | undefined> => {
  const filter = { textSearch: query };
  try {
    const { productsCollection } = await fetch(companyId, filter);
    return productsCollection
      .filter((product: any) => {
        // hide products which code starts with "G" for UHB service company
        if (APP_CONFIG.isUhbGroup && companyId === "8c1d50dc-85b8-48df-be85-fa680d698f9f") {
          return !product.sku.startsWith("G");
        }
        return true;
      })
      .map((product: any) => ({
        product: product.id,
        value: product.id,
        title: product.title,
        quantity: 0,
        sku: product.sku,
        supplierSku: product.supplier_sku,
        // A product made on the fly has no stored price yet.
        price: product.price ?? "",
      }));
  } catch (e) {
    return undefined;
  }
};

export default ProductsSelect;
